import { Button, Grid, TextField, Tooltip } from '@mui/material';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import ContactSelector from '../ContactSelector/ContactSelector';
import { cargoQuickInputSelector, resetCargoInput, setCharterer, setSelectedCargoId, setCargoType, setDwtMin, setDwtMax, setLoadPort, setDisport, setLay, setCan, setRemarks, setOrderDetail, setTriggerFocusChartererInput } from './reducers/CargoQuickInputReducer';
import CargoTypeSelector from '../CargoTypeSelector/CargoTypeSelector';
import CargoTypeModel from '../CargoTypeSelector/models/CargoTypeModel';
import { cargoTypeSelectorSelector } from '../CargoTypeSelector/reducers/CargoTypeSelectorReducer';
import DwtSelector from '../Tonnage/components/DwtSelector';
import PortSelector from '../PortSelector/PortSelector';
import { portSelectorSelector } from '../PortSelector/reducers/PortSelectorReducer';
import PortModel from '../PortSelector/models/PortModel';
import StartEndDatepicker from '../StartEndDatepicker/StartEndDatepicker';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import CargoApi from '../Cargo/api/CargoApi';
import CargoModel from '../Cargo/models/CargoModel';
import { setTriggerSave } from './reducers/TonnageQuickInputReducer';

const CargoQuickInput: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const cargoInputViewData = useSelector(cargoQuickInputSelector);
    let cargoTypeSelectorData = useSelector(cargoTypeSelectorSelector);
    let portSelectorData = useSelector(portSelectorSelector);

    useEffect(() => {
        if (cargoInputViewData.triggerSave > 0) {
            save();
            dispatch(setTriggerSave(0));
        }
    }, [cargoInputViewData.triggerSave]);

    useEffect(() => {
        if (cargoInputViewData.triggerRefreshInput > 0) {
            refreshInput();
        }
    }, [cargoInputViewData.triggerRefreshInput]);

    const refreshInput = () => {
        dispatch(resetCargoInput());
        dispatch(setSelectedCargoId(""));
        dispatch(setTriggerFocusChartererInput((new Date()).getTime()));
    }

    const getCargoById = (cargoTypeId: number) => {
        let selectedCargoType = _.find(cargoTypeSelectorData.cargoTypes, (cargo) => {
            return cargo.id === cargoTypeId;
        });

        if (selectedCargoType) {
            return selectedCargoType;
        }

        return null;
    }

    const getPortById = (portId: number) => {
        let selectedPort = _.find(portSelectorData.ports, (port) => {
            return port.id === portId;
        });

        if (selectedPort) {
            return selectedPort;
        }

        return null;
    }

    const save = () => {
        try {
            validate();

            CargoApi.Save(cargoInputViewData.cargoInput)
                .then((response: BaseResponse<CargoModel>) => {
                    if (response.success) {
                        if (cargoInputViewData.cargoInput.id.length === 0) {
                            Utility.Alert.Success("Tonnage added successful");
                        } else {
                            Utility.Alert.Success("Tonnage updated successful");
                        }

                        refreshInput();
                    } else {
                        Utility.Alert.Error(response.message);
                    }
                })
                .catch((error) => {
                    Utility.Alert.Error(error);
                });
        } catch (error) {
            Utility.Alert.Error(error);
        }
    }

    const validate = () => {
        if (cargoInputViewData.cargoInput.charterer.length === 0) {
            throw "Account name is required";
        }

        if (cargoInputViewData.cargoInput.cargoType === 0) {
            throw "Cargo type is required";
        }

        if (!cargoInputViewData.cargoInput.dwtMin || cargoInputViewData.cargoInput.dwtMin === 0) {
            throw "Dwt min is required";
        }

        if (!cargoInputViewData.cargoInput.dwtMax || cargoInputViewData.cargoInput.dwtMax === 0) {
            throw "Dwt max is required";
        }

        if (cargoInputViewData.cargoInput.loadPort === 0) {
            throw "Loadport is required";
        }

        if (cargoInputViewData.cargoInput.disport === 0) {
            throw "Disport is required";
        }

        if (cargoInputViewData.cargoInput.lay.length === 0) {
            throw "Lay is required";
        }

        if (cargoInputViewData.cargoInput.can.length === 0) {
            throw "Can is required";
        }
    }

    return (
        <>

            <Grid container>
                <Grid item xs={12}>
                    <Grid container className='cargo-input-component'>
                        <Grid xs={10} lg={9}>
                            <ContactSelector
                                label='Account Name'
                                selectedContactId={cargoInputViewData.cargoInput.charterer}
                                useFocusMode={true}
                                focusValue={cargoInputViewData.triggerFocusChartererInput}
                                onContactIdChange={(value: string) => {
                                    dispatch(setCharterer(value));
                                }}
                            >
                            </ContactSelector>
                        </Grid>
                        <Grid item xs={2} lg={3}>
                            <Tooltip title="Reset">
                                <Button
                                    className="clear-input-button"
                                    color="success"
                                    variant="contained"
                                    onClick={() => {
                                        refreshInput();
                                    }}
                                >
                                    <i className="fa fa-refresh" aria-hidden="true"></i>
                                </Button>
                            </Tooltip>
                        </Grid>
                    </Grid>
                    <CargoTypeSelector
                        className='cargo-input-component'
                        isGroupDisabled={true}
                        onChange={(cargoType: CargoTypeModel | null) => {
                            if (cargoType) {
                                dispatch(setCargoType((cargoType as CargoTypeModel).id));
                            }
                        }}
                        cargoTypeValue={getCargoById(cargoInputViewData.cargoInput.cargoType)}
                    >
                    </CargoTypeSelector>
                    <DwtSelector
                        dwtFromValue={cargoInputViewData.cargoInput.dwtMin}
                        dwtToValue={cargoInputViewData.cargoInput.dwtMax}
                        className="cargo-input-component"
                        dwtFromLabel='Dwt Min(kT)'
                        dwtToLabel='Dwt Max(kT)'
                        useSizeSelector={false}
                        onDwtFromChange={(value: number) => {
                            dispatch(setDwtMin(value));
                        }}
                        onDwtToChange={(value: number) => {
                            dispatch(setDwtMax(value));
                        }}
                    ></DwtSelector>
                </Grid>
                <Grid item xs={12}>
                    <Grid container>
                        <Grid item xs={6}>
                            <PortSelector
                                onPortChange={(value: PortModel | null) => {
                                    if (value) {
                                        dispatch(setLoadPort((value as PortModel).id));
                                    } else {
                                        dispatch(setLoadPort(0));
                                    }
                                }}
                                className="cargo-input-component"
                                portLabel='Loadport'
                                areaLabel='Area'
                                isAreaDisabled={true}
                                portValue={getPortById(cargoInputViewData.cargoInput.loadPort)}
                                isAreaRelatedToPort={true}
                                hideArea={true}
                            >
                            </PortSelector>
                        </Grid>
                        <Grid item xs={6}>
                            <PortSelector
                                onPortChange={(value: PortModel | null) => {
                                    if (value) {
                                        dispatch(setDisport((value as PortModel).id));
                                    } else {
                                        dispatch(setDisport(0));
                                    }
                                }}
                                className="cargo-input-component"
                                portLabel='Disport'
                                areaLabel='Area'
                                isAreaDisabled={true}
                                portValue={getPortById(cargoInputViewData.cargoInput.disport)}
                                isAreaRelatedToPort={true}
                                hideArea={true}
                            >
                            </PortSelector>
                        </Grid>
                    </Grid>
                    <StartEndDatepicker
                        startDateLabel="Lay"
                        endDateLabel="Can"
                        className='cargo-input-component'
                        useQuickEndDateInput={true}
                        onStartDateChange={(date: string) => {
                            if (date) {
                                dispatch(setLay(date));
                            }
                        }}
                        onEndDateChange={(date: string) => {
                            if (date) {
                                dispatch(setCan(date));
                            }
                        }}
                        startDateValue={cargoInputViewData.cargoInput.lay.toUpperCase()}
                        endDateValue={cargoInputViewData.cargoInput.can.toUpperCase()}
                    >
                    </StartEndDatepicker>
                </Grid>
                <Grid item xs={12}
                    className="cargo-input-component"
                >
                    <TextField
                        label="Full Order"
                        multiline
                        fullWidth
                        rows={2}
                        value={cargoInputViewData.cargoInput.orderDetail}
                        onChange={(event) => {
                            dispatch(setOrderDetail(event.target.value));
                        }}
                        className="tonnage-input-full-tc"
                    />
                </Grid>
                <Grid item xs={12}
                >
                    <Button
                        onClick={save}
                        variant="outlined"
                    >
                        Save (s)
                    </Button>
                </Grid>
            </Grid>
        </>
    )
};

export default CargoQuickInput;